/** ต้นทุนและผลต่าง (D-190) — ต้นทุนใบสั่งผลิต มาตรฐาน vs จริง, ต้นทุนต่อหน่วย (roll-up), ต้นทุนจัดซื้อ + PPV, ประวัติราคาซื้อ — อ่านอย่างเดียว */

import { Fragment, useEffect, useState } from 'react';

import { RecordTable, type RecordColumn } from '../components/RecordTable';
import { fetchRecords, queryString, type RecordRow } from '../../lib/backend/records';
import { callJson, stockFailureText } from '../../lib/inventory/stock';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { costElement, flattenOrderCost } from './orderCostRow';

export interface CostScreenProps {
  /** ราคาซื้อรายผู้ขายต้องมีสิทธิ์ดูทั้งบัญชีและจัดซื้อ (D-190) — ไม่ส่งมา = ซ่อนสองแท็บจัดซื้อ */
  readonly permissions?: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

type TabId = 'orders' | 'rollup' | 'purchase' | 'history' | 'costcenter';

const TABS: readonly { readonly id: TabId; readonly label: string; readonly prices?: boolean; readonly accounting?: boolean }[] = [
  { id: 'orders', label: 'ต้นทุนใบสั่งผลิต' },
  { id: 'rollup', label: 'ต้นทุนต่อหน่วยสินค้า' },
  { id: 'costcenter', label: 'ตาม Cost Center', accounting: true },
  { id: 'purchase', label: 'ต้นทุนจัดซื้อ', prices: true },
  { id: 'history', label: 'ประวัติราคาซื้อ', prices: true },
];

const ELEMENTS = ['material', 'labor', 'machine', 'outsource', 'total'] as const;
const ELEMENT_LABEL: Readonly<Record<(typeof ELEMENTS)[number], string>> = {
  material: 'วัตถุดิบ',
  labor: 'ค่าแรง',
  machine: 'ค่าเครื่อง',
  outsource: 'จ้างผลิต',
  total: 'รวม',
};

const ORDER_COLUMNS: readonly RecordColumn[] = [
  { key: 'order_no', label: 'ใบสั่งผลิต' },
  { key: 'product_code', label: 'สินค้า' },
  { key: 'status', label: 'สถานะ' },
  { key: 'good_quantity', label: 'ของดี' },
  { key: 'standard_total', label: 'มาตรฐาน' },
  { key: 'actual_total', label: 'จริง' },
  { key: 'variance_total', label: 'ผลต่าง' },
  { key: 'standard_unit_cost', label: 'มาตรฐาน/หน่วย' },
  { key: 'actual_unit_cost', label: 'จริง/หน่วย' },
  { key: 'missing_text', label: 'ข้อมูลที่ขาด' },
];

const PURCHASE_COLUMNS: readonly RecordColumn[] = [
  { key: 'key', label: 'รหัส' },
  { key: 'name', label: 'ชื่อ' },
  { key: 'line_count', label: 'บรรทัด' },
  { key: 'ordered_value', label: 'มูลค่าสั่ง' },
  { key: 'received_quantity', label: 'จำนวนรับ' },
  { key: 'received_value', label: 'มูลค่ารับ (ราคา PO)' },
  { key: 'standard_value', label: 'มูลค่ามาตรฐาน' },
  { key: 'price_variance', label: 'ผลต่างราคาซื้อ (PPV)' },
];

const HISTORY_COLUMNS: readonly RecordColumn[] = [
  { key: 'ordered_at', label: 'วันที่สั่ง' },
  { key: 'po_number', label: 'PO' },
  { key: 'line_no', label: 'บรรทัด' },
  { key: 'status', label: 'สถานะ' },
  { key: 'supplier_name', label: 'ผู้ขาย' },
  { key: 'quantity', label: 'จำนวน' },
  { key: 'unit_price', label: 'ราคา/หน่วย' },
  { key: 'standard_unit_cost', label: 'มาตรฐาน/หน่วย' },
  { key: 'price_variance_per_unit', label: 'ผลต่าง/หน่วย' },
];

const MATERIAL_COLUMNS: readonly RecordColumn[] = [
  { key: 'component_code', label: 'ชิ้นส่วน' },
  { key: 'qty_per_unit', label: 'ต่อหน่วย' },
  { key: 'scrap_factor', label: 'เผื่อเสีย' },
  { key: 'unit_cost', label: 'ต้นทุน/หน่วย' },
  { key: 'cost', label: 'ต้นทุน' },
];

const OPERATION_COLUMNS: readonly RecordColumn[] = [
  { key: 'operation_seq', label: 'ขั้นตอน' },
  { key: 'work_center_code', label: 'Work Center' },
  { key: 'std_time_minutes', label: 'เวลามาตรฐาน (นาที)' },
  { key: 'setup_time_minutes', label: 'ตั้งเครื่อง (นาที)' },
  { key: 'labor_cost', label: 'ค่าแรง' },
  { key: 'machine_cost', label: 'ค่าเครื่อง' },
];

function useRows(path: string, params: Readonly<Record<string, string>>, enabled: boolean, io: IoOptions) {
  const [state, setState] = useState<{ rows: readonly RecordRow[]; extra: RecordRow; error: string | null }>({
    rows: [],
    extra: {},
    error: null,
  });
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) {
      setState({ rows: [], extra: {}, error: null });
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await fetchRecords(path, params, io);
      if (cancelled) {
        return;
      }
      setState(
        result.status === 'ready'
          ? { rows: result.items, extra: result.extra, error: null }
          : { rows: [], extra: {}, error: stockFailureText(result) },
      );
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, enabled, io.apiBaseUrl, io.fetchImpl]);
  return state;
}

interface IoOptions {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

function ErrorNote({ error, testId }: { readonly error: string | null; readonly testId: string }) {
  return error === null ? null : (
    <p className="cap-notice__note" role="alert" data-testid={testId}>
      {error}
    </p>
  );
}

function OrderCosts({ plant, io }: { readonly plant: string; readonly io: IoOptions }) {
  const [selected, setSelected] = useState<string | null>(null);
  const { rows, error } = useRows('/v1/production-order-costs', { plant_code: plant }, plant !== '', io);
  const flat = rows.map(flattenOrderCost);
  const detail = flat.find((r) => r.order_no === selected);
  return (
    <>
      <ErrorNote error={error} testId="cost-orders-error" />
      <RecordTable rows={flat} columns={ORDER_COLUMNS} testId="cost-orders" />
      {flat.length > 0 && (
        <label className="cap-field">
          <span className="cap-field__label">ดูรายละเอียดใบสั่งผลิต</span>
          <select
            className="cap-input"
            aria-label="ดูรายละเอียดใบสั่งผลิต"
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">—</option>
            {flat.map((r) => (
              <option key={String(r.order_no)} value={String(r.order_no)}>
                {String(r.order_no)}
              </option>
            ))}
          </select>
        </label>
      )}
      {detail !== undefined && (
        <table className="cap-table" data-testid="cost-order-detail">
          <thead>
            <tr>
              <th>องค์ประกอบ</th>
              <th>ตามแผน</th>
              <th>มาตรฐาน (ของดี)</th>
              <th>จริง</th>
              <th>ผลต่าง</th>
            </tr>
          </thead>
          <tbody>
            {ELEMENTS.map((key) => (
              <tr key={key}>
                <td>{ELEMENT_LABEL[key]}</td>
                {(['planned', 'standard', 'actual', 'variance'] as const).map((group) => (
                  <td key={group}>{String(costElement(detail, group, key) ?? '—')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Rollup({ plant, io }: { readonly plant: string; readonly io: IoOptions }) {
  const [product, setProduct] = useState('');
  const [lotSize, setLotSize] = useState('1');
  const [result, setResult] = useState<RecordRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setResult(null);
    if (plant === '' || product.trim() === '') {
      setError('กรอกโรงงานและรหัสสินค้าก่อน');
      return;
    }
    const query = queryString({ plant_code: plant, product_code: product.toUpperCase(), lot_size: lotSize });
    const response = await callJson('GET', `/v1/product-cost-rollup${query}`, undefined, {}, io);
    if (response.status !== 'ok') {
      setError(stockFailureText(response));
      return;
    }
    setResult(typeof response.payload === 'object' && response.payload !== null ? (response.payload as RecordRow) : {});
  };

  const list = (key: string): readonly RecordRow[] =>
    result !== null && Array.isArray(result[key]) ? (result[key] as RecordRow[]) : [];

  return (
    <>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">รหัสสินค้า</span>
          <input
            className="cap-input"
            aria-label="รหัสสินค้า"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ขนาดล็อต</span>
          <input
            className="cap-input"
            aria-label="ขนาดล็อต"
            value={lotSize}
            onChange={(e) => setLotSize(e.target.value)}
          />
        </label>
        <button type="button" className="cap-button cap-button--primary" onClick={() => void run()}>
          คำนวณ
        </button>
      </div>
      <ErrorNote error={error} testId="cost-rollup-error" />
      {result !== null && (
        <div data-testid="cost-rollup">
          <dl className="cap-notice__details">
            <dt>วัตถุดิบ</dt>
            <dd>{String(result.material_cost ?? '—')}</dd>
            <dt>ค่าแรง</dt>
            <dd>{String(result.labor_cost ?? '—')}</dd>
            <dt>ค่าเครื่อง+โสหุ้ย</dt>
            <dd>{String(result.machine_cost ?? '—')}</dd>
            <dt>รวมต่อหน่วย</dt>
            <dd data-testid="cost-rollup-total">{String(result.total_cost ?? '—')}</dd>
            <dt>ต้นทุนมาตรฐานปัจจุบัน</dt>
            <dd>{String(result.current_unit_cost ?? '—')}</dd>
            <dt>ต่างจากมาตรฐานปัจจุบัน</dt>
            <dd>{String(result.difference_from_unit_cost ?? '—')}</dd>
          </dl>
          {Array.isArray(result.missing) && result.missing.length > 0 && (
            <p className="cap-notice__note" data-testid="cost-rollup-missing">
              ข้อมูลยังไม่ครบ: {(result.missing as unknown[]).join(', ')}
            </p>
          )}
          <RecordTable rows={list('materials')} columns={MATERIAL_COLUMNS} testId="cost-rollup-materials" />
          <RecordTable rows={list('operations')} columns={OPERATION_COLUMNS} testId="cost-rollup-operations" />
        </div>
      )}
    </>
  );
}

const CC_TYPE_TEXT: Readonly<Record<string, string>> = { PRODUCTION: 'ผลิต', SUPPORT: 'สนับสนุน', ADMIN: 'บริหาร' };

const asRows = (v: unknown): readonly RecordRow[] =>
  Array.isArray(v) ? v.filter((x): x is RecordRow => typeof x === 'object' && x !== null) : [];

/** ตัวเลขเงิน/นาทีเป็น decimal string จาก API — แสดงตามที่ได้ ไม่แปลงเป็น Number */
const txt = (v: unknown): string => (v === null || v === undefined || v === '' ? '—' : String(v));

function CostCenterCosts({ plant, io }: { readonly plant: string; readonly io: IoOptions }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const ready = plant !== '' && from !== '' && to !== '';
  const { rows, extra, error } = useRows('/v1/cost-center-costs', { plant_code: plant, from, to }, ready, io);
  const totals = typeof extra.totals === 'object' && extra.totals !== null ? (extra.totals as RecordRow) : null;
  const unrated = Array.isArray(extra.unrated_work_centers) ? (extra.unrated_work_centers as unknown[]).map(String) : [];
  const toggle = (code: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(code)) {
        next.add(code);
      }
      return next;
    });
  return (
    <>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">ตั้งแต่วันที่</span>
          <input className="cap-input" type="date" aria-label="ตั้งแต่วันที่" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ถึงวันที่</span>
          <input className="cap-input" type="date" aria-label="ถึงวันที่" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      {!ready && (
        <p className="cap-notice__text" data-testid="cost-cc-need-window">
          กรอกโรงงานและช่วงวันที่ก่อน (ไม่เกิน 92 วัน)
        </p>
      )}
      <ErrorNote error={error} testId="cost-cc-error" />
      {ready && unrated.length > 0 && (
        <p className="cap-notice__note" data-testid="cost-cc-unrated">
          Work Center ที่ยังไม่ได้ตั้งอัตราค่าแรง/ค่าเครื่อง (ต้นทุนอาจต่ำกว่าจริง): {unrated.join(', ')}
        </p>
      )}
      {ready && (
        <table className="cap-table" data-testid="cost-cc-table">
          <thead>
            <tr>
              <th />
              <th>Cost Center</th>
              <th>ชื่อ</th>
              <th>ประเภท</th>
              <th>ค่าแรง</th>
              <th>ค่าเครื่อง</th>
              <th>ต้นทุนผลิต</th>
              <th>นาทีแรงงาน</th>
              <th>นาทีเครื่อง</th>
              <th>เดบิตบัญชี</th>
              <th>เครดิตบัญชี</th>
              <th>สุทธิบัญชี</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={12}>ไม่มีรายการ</td>
              </tr>
            )}
            {rows.map((r) => {
              const code = String(r.cost_center_code ?? '');
              const wcs = asRows(r.work_centers);
              const isOpen = open.has(code);
              return (
                <Fragment key={code}>
                  <tr data-testid={`cost-cc-row-${code}`}>
                    <td>
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        aria-expanded={isOpen}
                        aria-label={`ดู Work Center ของ ${code}`}
                        onClick={() => toggle(code)}
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                    </td>
                    <td>{code}</td>
                    <td>{txt(r.name)}</td>
                    <td>{CC_TYPE_TEXT[String(r.cost_center_type)] ?? txt(r.cost_center_type)}</td>
                    <td>{txt(r.labor_cost)}</td>
                    <td>{txt(r.machine_cost)}</td>
                    <td>{txt(r.production_cost)}</td>
                    <td>{txt(r.labor_minutes)}</td>
                    <td>{txt(r.machine_minutes)}</td>
                    <td>{txt(r.accounting_debit)}</td>
                    <td>{txt(r.accounting_credit)}</td>
                    <td>{txt(r.accounting_net)}</td>
                  </tr>
                  {isOpen &&
                    wcs.map((w) => (
                      <tr key={String(w.work_center_code)} data-testid={`cost-cc-wc-${code}-${String(w.work_center_code)}`}>
                        <td />
                        <td colSpan={3}>Work Center {txt(w.work_center_code)}</td>
                        <td>{txt(w.labor_cost)}</td>
                        <td>{txt(w.machine_cost)}</td>
                        <td colSpan={2}>เวลา {txt(w.minutes)} นาที</td>
                        <td colSpan={4} />
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
          {totals !== null && (
            <tfoot>
              <tr data-testid="cost-cc-totals">
                <td />
                <td colSpan={3}>รวม</td>
                <td>{txt(totals.labor_cost)}</td>
                <td>{txt(totals.machine_cost)}</td>
                <td>{txt(totals.production_cost)}</td>
                <td colSpan={4} />
                <td>{txt(totals.accounting_net)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </>
  );
}

function PurchaseCosts({ plant, io }: { readonly plant: string; readonly io: IoOptions }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState<'supplier' | 'product'>('supplier');
  const ready = plant !== '' && from !== '' && to !== '';
  const { rows, extra, error } = useRows(
    '/v1/purchase-costs',
    { plant_code: plant, date_from: from, date_to: to, group_by: groupBy },
    ready,
    io,
  );
  const totals = typeof extra.totals === 'object' && extra.totals !== null ? [extra.totals as RecordRow] : [];
  return (
    <>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">ตั้งแต่วันที่</span>
          <input
            className="cap-input"
            type="date"
            aria-label="ตั้งแต่วันที่"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">ถึงวันที่</span>
          <input
            className="cap-input"
            type="date"
            aria-label="ถึงวันที่"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">จัดกลุ่มตาม</span>
          <select
            className="cap-input"
            aria-label="จัดกลุ่มตาม"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value === 'product' ? 'product' : 'supplier')}
          >
            <option value="supplier">ผู้ขาย</option>
            <option value="product">สินค้า</option>
          </select>
        </label>
      </div>
      {!ready && (
        <p className="cap-notice__text" data-testid="cost-purchase-need-window">
          กรอกโรงงานและช่วงวันที่ก่อน (ไม่เกิน 92 วัน)
        </p>
      )}
      <ErrorNote error={error} testId="cost-purchase-error" />
      {ready && (
        <>
          <RecordTable rows={rows} columns={PURCHASE_COLUMNS} testId="cost-purchase" />
          <RecordTable rows={totals} columns={PURCHASE_COLUMNS} testId="cost-purchase-totals" empty="—" />
        </>
      )}
    </>
  );
}

function PriceHistory({ plant, io }: { readonly plant: string; readonly io: IoOptions }) {
  const [product, setProduct] = useState('');
  const code = product.trim().toUpperCase();
  const { rows, error } = useRows(
    '/v1/purchase-price-history',
    { plant_code: plant, product_code: code },
    plant !== '' && code !== '',
    io,
  );
  return (
    <>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">รหัสสินค้า</span>
          <input
            className="cap-input"
            aria-label="รหัสสินค้า"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
          />
        </label>
      </div>
      <ErrorNote error={error} testId="cost-history-error" />
      <RecordTable rows={rows} columns={HISTORY_COLUMNS} testId="cost-history" />
    </>
  );
}

export function CostScreen({ permissions, apiBaseUrl, fetchImpl }: CostScreenProps) {
  const seesPrices = permissions !== undefined && can(permissions, 'PROCUREMENT', 'VIEW');
  const seesAccounting = permissions !== undefined && can(permissions, 'ACCOUNTING', 'VIEW');
  const tabs = TABS.filter((t) => (seesPrices || t.prices !== true) && (seesAccounting || t.accounting !== true));
  const [tabId, setTabId] = useState<TabId>('orders');
  const [plant, setPlant] = useState('');
  const io: IoOptions = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };
  const plantCode = plant.trim();

  return (
    <div className="cap-page" data-testid="cost-screen">
      <div className="cap-page__head">
        <h1 className="cap-page__title">ต้นทุนและผลต่าง</h1>
      </div>
      <p className="cap-notice__text">
        ต้นทุนมาตรฐานเทียบของจริง คำนวณตอนเปิดดู ยังไม่ลงบัญชี — เวลาใน routing เป็นนาที (D-190)
      </p>
      <div className="cap-filters" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tabId}
            className={`cap-button ${t.id === tabId ? 'cap-button--primary' : 'cap-button--ghost'}`}
            onClick={() => setTabId(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">รหัสโรงงาน</span>
          <input
            className="cap-input"
            aria-label="รหัสโรงงาน"
            value={plant}
            onChange={(e) => setPlant(e.target.value.toUpperCase())}
          />
        </label>
      </div>
      {tabId === 'orders' && <OrderCosts plant={plantCode} io={io} />}
      {tabId === 'rollup' && <Rollup plant={plantCode} io={io} />}
      {tabId === 'costcenter' && seesAccounting && <CostCenterCosts plant={plantCode} io={io} />}
      {tabId === 'purchase' && seesPrices && <PurchaseCosts plant={plantCode} io={io} />}
      {tabId === 'history' && seesPrices && <PriceHistory plant={plantCode} io={io} />}
    </div>
  );
}
